import { GeoJsonLayer } from "@deck.gl/layers";
import DeckGL from "@deck.gl/react";
import {
    faCircleInfo,
    faMapLocation,
    faMinus,
    faPlus,
    faRuler,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpiral } from "@fortawesome/sharp-regular-svg-icons";
import bboxPolygon from "@turf/bbox-polygon";
import difference from "@turf/difference";
import { feature, featureCollection } from "@turf/helpers";
import type { Polygon } from "geojson";
import { useMemo, useState } from "react";
import MapGL from "react-map-gl/maplibre";
import { useAppSelector } from "../../../hooks";
import { basemaps } from "../basemaps";
import {
    CROSSHAIR_ICONS_NO_CONTRAST,
    EMISSION_BINS,
    MAP_ZOOM_SHOW_DETAILS,
} from "../constants";
import { ControlContainer } from "../elements/common";
import { useMap } from "../hooks/mapState";
import { EXTRA_DATA_NAVY } from "../layers/extra";
import { infrastructureIconAtlas } from "../layers/infrastructure";
import { ControlButton } from "../ui/MapControls";

const SITE_ICON_DATA_URL = infrastructureIconAtlas().toDataURL();

type ExtraContext = "MINIMAP" | "LEGEND";

const EmissionItemLegend = (props: { label: string; icon: any }) => (
    <li className="mb-2 flex gap-4 items-center text-sm">
        <img alt={props.label} src={props.icon} className="h-5" />
        {props.label}
    </li>
);

/**
 * White disc with a navy outline — the shared shape of the extra-data map
 * marks, both the single-survey icon and the cluster bubble.
 */
const ExtraDataDisc = (props: { children: React.ReactNode }) => (
    <div
        className={`
            h-4 w-4 shrink-0 rounded-full bg-white border-[1.5px]
            flex items-center justify-center
        `}
        style={{ borderColor: EXTRA_DATA_NAVY, color: EXTRA_DATA_NAVY }}
    >
        {props.children}
    </div>
);

const LegendItem = (props: { label: string; swatch: React.ReactNode }) => (
    <li className="mb-0.5 flex gap-4 items-center text-sm">
        <div className="h-7 w-16 flex items-center justify-center shrink-0">
            {props.swatch}
        </div>
        {props.label}
    </li>
);

const Legend = () => {
    const { debounced } = useMap("mainMap");
    const canViewExtraData = useAppSelector((s) =>
        s.auth.flags.includes("enable_area_surveys"),
    );
    const canViewUnmatchedPoints = useAppSelector((s) =>
        s.auth.flags.includes("unmatched_emissions"),
    );
    const overviewsEnabled = useAppSelector((s) =>
        s.auth.flags.includes("enable_overviews"),
    );
    return (
        <div className="rounded p-2 bg-white min-h-36 text-sm w-60">
            <div className="px-1 text-sm font-semibold">Legend</div>
            <hr className="my-2" />
            {debounced.viewState.zoom < MAP_ZOOM_SHOW_DETAILS && (
                <div className="mb-6 px-4 flex items-start justify-between gap-2">
                    {EMISSION_BINS.map((bin) => (
                        <div
                            key={bin.label}
                            className="flex flex-col justify-center items-center"
                        >
                            <div className="h-12 flex justify-center items-center">
                                <div
                                    className={`
                                        border-2 rounded-full
                                        bg-gray-300 border-ae-blue-900
                                    `}
                                    style={{
                                        width: `${bin.size}px`,
                                        height: `${bin.size}px`,
                                    }}
                                    title={bin.label}
                                />
                            </div>
                            <div className="flex items-center justify-center">
                                {bin.label}
                            </div>
                            <div className="flex items-center justify-center">
                                kg/h
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <ul className="px-2">
                <EmissionItemLegend
                    label="Third Party"
                    icon={CROSSHAIR_ICONS_NO_CONTRAST.thirdParty}
                />
                <EmissionItemLegend
                    label="Company Monitoring"
                    icon={CROSSHAIR_ICONS_NO_CONTRAST.operatorProvided}
                />
                <EmissionItemLegend
                    label="EPA Super Emitter Program"
                    icon={CROSSHAIR_ICONS_NO_CONTRAST.epa}
                />
                {canViewUnmatchedPoints && (
                    <EmissionItemLegend
                        label="Unmatched Company Monitoring"
                        icon={CROSSHAIR_ICONS_NO_CONTRAST.unmatchedDataPoints}
                    />
                )}
            </ul>
            <hr className="my-2" />
            <ul>
                {overviewsEnabled && (
                    <LegendItem
                        label="Areas with sites"
                        swatch={
                            <div className="grid grid-cols-2">
                                <div className="bg-black h-[8px] w-[8px]" />
                                <div className="bg-gray-600 h-[8px] w-[8px]" />
                                <div className="bg-gray-400 h-[8px] w-[8px]" />
                                <div className="bg-gray-300 h-[8px] w-[8px]" />
                            </div>
                        }
                    />
                )}
                <LegendItem
                    label="Site"
                    swatch={
                        <img
                            alt="Site"
                            src={SITE_ICON_DATA_URL}
                            className="mt-2 w-10 object-contain"
                        />
                    }
                />
                <LegendItem
                    label="Equipment Group"
                    swatch={
                        <div className="h-4 w-7 rounded-sm border border-dashed border-black" />
                    }
                />
                <LegendItem
                    label="Equipment"
                    swatch={
                        <div className="h-2.5 w-2.5 rounded-full bg-white border-[1.5px] border-black" />
                    }
                />
                <LegendItem
                    label="Pipeline"
                    swatch={<div className="h-0 w-8 border-t-2 border-black" />}
                />
                {canViewExtraData && (
                    <LegendItem
                        label="Champion X Surveys"
                        swatch={
                            <div className="flex items-center gap-1">
                                <ExtraDataDisc>
                                    <FontAwesomeIcon
                                        icon={faSpiral}
                                        className="h-2.5"
                                    />
                                </ExtraDataDisc>
                                <span className="text-xs text-gray-400">/</span>
                                <ExtraDataDisc>
                                    <span className="text-[7px] font-medium">
                                        12
                                    </span>
                                </ExtraDataDisc>
                            </div>
                        }
                    />
                )}
            </ul>
        </div>
    );
};

const MiniMap = () => {
    const { debounced } = useMap("mainMap");
    const layer = useMemo(() => {
        const worldPolygon = bboxPolygon([-180, -90, 180, 90]);
        return new GeoJsonLayer({
            id: "infrastructure",
            data: difference(
                featureCollection([
                    worldPolygon,
                    feature(debounced.areaOnScreen as Polygon),
                ]),
            ),
            pointType: "circle",
            filled: true,
            getPointRadius: 6,
            pointRadiusUnits: "pixels",
            lineWidthUnits: "pixels",
            stroked: true,
            autoHighlight: true,
            highlightColor: [0, 0, 0, 30],
            getFillColor: [255, 255, 255, 30],
            getLineWidth: 2,
            getLineColor: [0, 0, 0, 255],
        });
    }, [debounced.areaOnScreen]);

    return (
        <div className="relative h-44 w-60 rounded overflow-hidden">
            <DeckGL
                viewState={
                    {
                        ...debounced.viewState,
                        zoom: Math.max(1, debounced.viewState.zoom - 6),
                    } as any
                }
                controller={false}
                layers={[layer]}
            >
                <MapGL
                    mapStyle={basemaps.esri.mapStyle as any}
                    attributionControl={false}
                />
            </DeckGL>
        </div>
    );
};

/**
 * ZoomAndLegend
 *
 * FIXME: currently, this component is only usable in the map map.
 */
export const ZoomAndLegend = ({
    zoomIn,
    zoomOut,
    isMeasuring,
    toggleMeasuring,
}: {
    zoomIn: () => void;
    zoomOut: () => void;
    isMeasuring: boolean;
    toggleMeasuring: () => void;
}) => {
    const [showExtra, setShowExtra] = useState<ExtraContext>();

    const toggle = (controlName: ExtraContext) => {
        if (controlName === showExtra) {
            setShowExtra(undefined);
        } else {
            setShowExtra(controlName);
        }
    };

    return (
        <ControlContainer className="gap-2 p-1 items-end pointer-events-auto">
            {showExtra === "MINIMAP" && <MiniMap />}
            {showExtra === "LEGEND" && <Legend />}
            <div className="flex flex-col gap-3">
                <div className="flex flex-col rounded divide-x">
                    <ControlButton className="w-8 rounded-t" onClick={zoomIn}>
                        <FontAwesomeIcon icon={faPlus} className="h-4" />
                    </ControlButton>
                    <ControlButton
                        className="w-8"
                        onClick={() => toggle("MINIMAP")}
                    >
                        <FontAwesomeIcon icon={faMapLocation} className="h-4" />
                    </ControlButton>
                    <ControlButton className="w-8 rounded-b" onClick={zoomOut}>
                        <FontAwesomeIcon icon={faMinus} className="h-4" />
                    </ControlButton>
                </div>

                <ControlButton
                    className="w-8 rounded"
                    onClick={() => toggleMeasuring()}
                    selected={isMeasuring}
                >
                    <FontAwesomeIcon icon={faRuler} className="h-4" />
                </ControlButton>

                <ControlButton
                    className="w-8 rounded"
                    onClick={() => toggle("LEGEND")}
                >
                    <FontAwesomeIcon icon={faCircleInfo} className="h-4" />
                </ControlButton>
            </div>
        </ControlContainer>
    );
};

interface ZoomControlProps {
    zoomIn: () => void;
    zoomOut: () => void;
}

export const ZoomControlBase = (props: ZoomControlProps) => (
    <div className="flex flex-col gap-3">
        <div className="flex flex-col rounded divide-y">
            <ControlButton
                className="w-8 rounded-t"
                onClick={(e) => {
                    e.stopPropagation();
                    props.zoomIn();
                }}
            >
                <FontAwesomeIcon icon={faPlus} className="h-4" />
            </ControlButton>
            <ControlButton
                className="w-8 rounded-b"
                onClick={(e) => {
                    e.stopPropagation();
                    props.zoomOut();
                }}
            >
                <FontAwesomeIcon icon={faMinus} className="h-4" />
            </ControlButton>
        </div>
    </div>
);
